/**
 * Univer-specific spreadsheet utilities: cell value type mapping, Univer cell shape
 * conversion, and helpers that depend on @univerjs/core types.
 */

import {
  CellValueType,
  HorizontalAlign,
  RichTextValue,
  VerticalAlign,
  WrapStrategy
} from '@univerjs/core';
import type { Cell, Column } from '../../api/spreadsheet';
import type { EnrichingCellItem } from '../../api/spreadsheet';
import { PRIMARY_COLUMN_NAMES } from '../../api/spreadsheet';
import {
  forceBooleanTrueFalse,
  formatListValueForDisplay,
  normalizePythonType,
  validateCell
} from '../../utils/spreadsheetValidation';
import type { UniverCellShape } from './spreadsheetTypes';
import type { ColumnSchemaWithData } from './spreadsheetUtils';
import {
  getColTypeFromSchema,
  getColumnIndexMapping,
  normalizeCellValue
} from './spreadsheetUtils';

export type { UniverCellShape } from './spreadsheetTypes';

// Numeric-looking values that should be sorted by numeric value.
// Supports: integers, decimals, and exponent notation (e.g. 1e6, -1.2E-3).
const NUMERIC_STRING_REGEX = /^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?$/;

function parseNumericString(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!NUMERIC_STRING_REGEX.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Map backend col_type to Univer CellValueType so cells are not auto-converted (e.g. date string → serial). */
export function getUniverCellValueType(
  colType: string | undefined
): CellValueType {
  const n = normalizePythonType(colType ?? '');
  if (n === 'int' || n === 'float' || n === 'num' || n === 'number')
    return CellValueType.NUMBER;
  if (n === 'bool') return CellValueType.BOOLEAN;
  // Use FORCE_STRING for date so Univer never converts date strings to date serials
  if (n === 'date') return CellValueType.FORCE_STRING;
  return CellValueType.STRING; // str, url, email, etc.
}

/**
 * For number/boolean/date/url/email: use STRING when value is invalid so Univer shows the text
 * instead of coercing (e.g. NaN, false); validation will flag the error.
 * Also use STRING for empty values in number/boolean columns so empty cells don't display as 0/false.
 */
export function getEffectiveCellValueType(
  colType: string | undefined,
  value: string | number | boolean | null | undefined
): CellValueType {
  const base = getUniverCellValueType(colType);
  const isEmpty = value == null || value === '';
  if (
    isEmpty &&
    (base === CellValueType.NUMBER || base === CellValueType.BOOLEAN)
  )
    return CellValueType.STRING;

  // Special-case: for plain string columns, detect numeric-looking strings so sorting behaves numerically
  // (e.g. "10" sorts after "2" instead of lexicographically).
  const normalizedType = normalizePythonType(colType);
  if (normalizedType === 'str' && typeof value === 'string') {
    if (parseNumericString(value) != null) return CellValueType.NUMBER;
  }
  if (
    normalizedType === 'str' &&
    typeof value === 'number' &&
    Number.isFinite(value)
  ) {
    return CellValueType.NUMBER;
  }

  const result = validateCell(value, colType);
  if (result.valid) return base;
  return CellValueType.STRING;
}

/**
 * Build a single Univer cell value: rich text (p) when rawVal contains newlines (after trim),
 * otherwise plain (v). Trims rawVal so a trailing \n doesn't create an extra empty paragraph and break drawing.
 */
export function cellValueToUniverCell(
  rawVal: string,
  options: { colType?: string; s?: Record<string, unknown> } = {}
): UniverCellShape {
  const colType = options.colType ?? 'str';
  const baseS = { tb: WrapStrategy.WRAP as const, ...(options.s ?? {}) };
  const trimmed = typeof rawVal === 'string' ? rawVal.trimEnd() : rawVal;
  if (typeof trimmed === 'string' && /[\r\n]/.test(trimmed)) {
    const dataStream = trimmed.replace(/\r\n/g, '\r').replace(/\n/g, '\r');
    const stream = dataStream.endsWith('\r\n')
      ? dataStream
      : dataStream.endsWith('\r')
        ? dataStream + '\n'
        : dataStream + '\r\n';
    const paragraphs: { startIndex: number }[] = [];
    for (let i = 0; i < stream.length; i++) {
      if (stream[i] === '\r') paragraphs.push({ startIndex: i });
    }
    const body = { dataStream: stream, paragraphs };
    const richText = RichTextValue.createByBody(body);
    return {
      p: richText.getData(),
      v: null,
      t: getEffectiveCellValueType(colType, trimmed),
      s: baseS
    };
  }
  const effectiveType = getEffectiveCellValueType(colType, trimmed);
  if (effectiveType === CellValueType.NUMBER && typeof trimmed === 'string') {
    // Ensure `v` is numeric when sorting as numbers.
    const n = parseNumericString(trimmed);
    return { v: n ?? trimmed, t: effectiveType, s: baseS };
  }
  if (effectiveType === CellValueType.BOOLEAN && typeof trimmed === 'string') {
    const booleanValue = forceBooleanTrueFalse(trimmed);
    if (booleanValue != null) {
      const s = {
        tb: WrapStrategy.WRAP as const,
        vt: VerticalAlign.MIDDLE,
        ht: HorizontalAlign.CENTER,
        ...(options.s ?? {})
      };
      return { v: booleanValue, t: CellValueType.FORCE_STRING, s };
    }
  }
  return { v: trimmed, t: effectiveType, s: baseS };
}

/** For primary columns (e.g. domain name): keep only the first line; strip everything after any \n so only one URL shows. */
export function firstLineOnly(s: string): string {
  return s.split(/\r?\n/)[0].trimEnd();
}

/** Map backend cells to Univer row->col->cell shape (for set-range-values). */
export function mapCellsToUniver(
  columns: Column[],
  cells: Cell[]
): Record<number, Record<number, UniverCellShape>> {
  const columnMapping = getColumnIndexMapping(columns);
  const output: Record<number, Record<number, UniverCellShape>> = {};
  for (const cell of cells) {
    const columnNum = columnMapping.get(cell.column_name) ?? -1;
    if (columnNum < 0) continue;
    if (!output[cell.row_id]) output[cell.row_id] = {};
    const colType =
      getColTypeFromSchema(
        columns[columnNum] as ColumnSchemaWithData | undefined
      ) ?? 'str';
    let rawVal = normalizeCellValue(cell.cell_value);
    if (PRIMARY_COLUMN_NAMES.includes(cell.column_name))
      rawVal = firstLineOnly(rawVal);
    if (normalizePythonType(colType) === 'list')
      rawVal = formatListValueForDisplay(rawVal);
    output[cell.row_id][columnNum] = cellValueToUniverCell(rawVal, { colType });
  }
  return output;
}

/**
 * Convert API response from GET .../cells/execute/{workflow_id}/enriching to the Set<string>
 * format used by enrichingCellsRef: each key is "row_id:colIndex" where colIndex is the column
 * index for column_name in columnNamesByIndex. Items whose column is not in columnNamesByIndex are skipped.
 */
export function enrichingApiResponseToCellKeys(
  items: EnrichingCellItem[],
  columnNamesByIndex: string[]
): Set<string> {
  const set = new Set<string>();
  for (const { column_name, row_id } of items) {
    const col = columnNamesByIndex.indexOf(column_name);
    if (col >= 0) set.add(`${row_id}:${col}`);
  }
  return set;
}

/** Build a row->col->value map for validation from Univer cell data. */
export function cellMatrixFromUniverCells(
  univerCells: Record<number, Record<number, { v?: unknown; p?: unknown }>>
): Record<number, Record<number, unknown>> {
  const matrix: Record<number, Record<number, unknown>> = {};
  for (const rowKey of Object.keys(univerCells)) {
    const row = Number(rowKey);
    matrix[row] = {};
    const rowData = univerCells[row];
    for (const colKey of Object.keys(rowData)) {
      const col = Number(colKey);
      const cell = rowData[col];
      matrix[row][col] = cell != null ? normalizeCellValue(cell) : '';
    }
  }
  return matrix;
}
